import React, { useEffect, useState } from "react";
import AdminHOC from "../../shared/HOC/AdminHOC";
import { Input, Typography, Modal, message, Row, Col } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import CourseTable from "../../shared/table/CourseTable";
import CoursesModal from "./BooksModal";
import {
  fetchAllCourses,
  deleteCourse,
  updateCourse,
} from "../../../service/AdminService/BookService.js";
import handleCreateCourse from "./BooksModal";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../shared/button/CustomButton.jsx";
import CustomBreadcrumb from "../../shared/breadcrumbs/CustomBreadCrumb";
import Back from "../../../assets/back-btn.svg";
import "./CourseAdmin.css";
import CustomInput from "../../shared/Input/Custom-Input.jsx";
import LottieIcon from "../../shared/lottieIcon/LottieIcon.jsx";
import searchIconsvg from "../../../assets/icons/Search-Icon.svg";
import CustomTab from "../../shared/tab/TabComponent.jsx";

const { Title } = Typography;

const CoursesAdmin = ({
  setLoading,
  setHeader,
  setBreadCrumbs,
}) => {
  const [search, setSearch] = useState("");
  const [courseList, setCourseList] = useState([]);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [isInputHovered, setIsInputHovered] = useState(false);
  const [totalElements, setTotalElements] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [activeTab, setActiveTab] = useState("1");
  const [courseStatus, setCourseStatus] = useState(true);

  const navigate = useNavigate();

  const loadCourses = async (
    page = currentPage,
    size = pageSize,
    sort = sortBy,
    dir = sortDir,
    query = search,
    status = courseStatus
  ) => {
    try {
      setLoading(true);
      const response = await fetchAllCourses(page - 1, size, sort, dir, query, status);

      if (response) {
        setCourseList(response);
        setTotalElements(response.totalElements);
        setFilteredCourses(response)
      } else {
        setCourseList([]);
        setFilteredCourses([]);
        setTotalElements(0);
      }
    } catch (err) {
      console.error("Error loading courses:", err);
      setCourseList([]);
      setFilteredCourses([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (pagination) {
      if (pagination.pageSize !== pageSize) {
        setPageSize(pagination.pageSize);
        setCurrentPage(1);
      } else {
        setCurrentPage(pagination.current);
      }
    }

    if (sorter && sorter.field) {
      setSortBy(sorter.field);
      setSortDir(sorter.order === "ascend" ? "asc" : "desc");
    }
  };

  useEffect(() => {
    loadCourses(currentPage, pageSize, sortBy, sortDir, search, courseStatus);
  }, [currentPage, pageSize, sortBy, sortDir, courseStatus]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      loadCourses(1, pageSize, sortBy, sortDir, search, courseStatus);
    }, 500);
    return () => clearTimeout(timeout);
  }, [search]);



  const handleDeleteCourse = async (courseId) => {
    try {
      setLoading(true);
      const data = await deleteCourse(courseId);
      message.success(data?.message || "Course deleted successfully!");
      await loadCourses();
    } catch (error) {
      message.error(error?.message || "Course cannot be deleted.");
    } finally {
      setLoading(false);
    }
  };

  const showConfirmDelete = (course) => {
    Modal.confirm({
      title: "Are you sure you want to delete this course?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      zIndex: 'unset',
      onOk: () => handleDeleteCourse(course.courseId),
    });
  };

  const handleEditCourse = async (updatedCourse) => {
    try {
      setLoading(true);
      const data = await updateCourse(updatedCourse.courseId, {
        ...updatedCourse,
        active: updatedCourse.isActive,
      });
      message.success(data?.message || "Course updated successfully!");
      await loadCourses();
    } catch (error) {
      message.error(error?.message || "Course update failed.");
    } finally {
      setIsEditPopupOpen(false);
      setEditingCourse(null);
      setLoading(false);
    }
  };

  const handleOpenEditPopup = (course) => {
    setEditingCourse(course);
    setIsEditPopupOpen(true);
  };

  const handleOpenModal = () => {
    setEditingCourse(null);
    setIsEditPopupOpen(true);
  };

  const tabList = [
    { key: "1", label: "Active Courses" },
    { key: "2", label: "Inactive Courses" },
  ];

  const handleTabChange = (key) => {
    setActiveTab(key);
    setCourseStatus(key === "1");
    setCurrentPage(1);
  };

  return (
    <>
      <div className="admin-section">
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Row>
            <Col span={7}>
              <CustomTab entries={tabList} onChange={handleTabChange} />
            </Col>

            <Col span={17}>
              <div className="action-buttons" style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <div className="search-container">
                  <CustomInput
                    icon={
                      isInputHovered ? (
                        <LottieIcon name="searchIcon" size={24} shouldPlay={true} />
                      ) : (
                        <img src={searchIconsvg} alt="search" style={{ width: 24, height: 24 }} />
                      )
                    }
                    placeholder="Search by name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: 300 }}
                  />
                </div>
                <CustomButton onClick={handleOpenModal} disabled={!courseStatus}>
                  Add Course
                </CustomButton>
                <CustomButton
                  icon={<ExportOutlined />}
                  onClick={() => navigate("/course-report")}
                  disabled={!courseStatus}
                >
                  Report
                </CustomButton>
              </div>
            </Col>

            <Row style={{ width: "100%" }}>
              <div>
                {filteredCourses && filteredCourses.length > 0 ? (
                  <CourseTable
                    totalElements={totalElements}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    entries={filteredCourses}
                    onTableChange={handleTableChange}
                    onDeleteClick={showConfirmDelete}
                    onEditClick={handleOpenEditPopup}
                    setTotalElements={setTotalElements}
                  />
                ) : (
                  <div className="no-data-found">
                    {!courseStatus ? "No Inactive Courses Found" : "No Active Courses Found"}
                  </div>
                )}
              </div>
            </Row>
          </Row>
        </div>
      </div>

      <CoursesModal
        isModalOpen={isEditPopupOpen}
        handleCloseModal={() => setIsEditPopupOpen(false)}
        selectedCourse={editingCourse}
        handleCreateCourse={handleCreateCourse}
        handleUpdateCourse={handleEditCourse}
        setLoading={setLoading}
        title={editingCourse ? "Edit Course" : "Add New Course"}
        loadCourses={loadCourses}
      />
    </>
  );
};

export default AdminHOC(CoursesAdmin);